import { useLayoutEffect } from "react";

let activeLocks = 0;
let lockedScrollY = 0;
let lockedSidebarScroll: Array<{ element: HTMLElement; scrollTop: number }> = [];
let restoreFrame: number | undefined;
const scrollKeys = new Set(["ArrowDown", "ArrowUp", "End", "Home", "PageDown", "PageUp", " "]);
const LAYER_SCROLL_SELECTOR =
  ".record-side-panel, .MuiDialog-paper, .MuiPopover-paper, .MuiMenu-paper, .filter-dropdown-menu-list, .sidebar.menu-open .mobile-menu-panel";

function closestScrollLayer(target: EventTarget | null) {
  return target instanceof Element
    ? target.closest<HTMLElement>(LAYER_SCROLL_SELECTOR)
    : null;
}

function elementCanConsumeWheel(element: HTMLElement, event: WheelEvent) {
  const style = window.getComputedStyle(element);
  const scrollableY = ["auto", "scroll", "overlay"].includes(style.overflowY);
  const scrollableX = ["auto", "scroll", "overlay"].includes(style.overflowX);
  const canMoveY =
    scrollableY &&
    element.scrollHeight > element.clientHeight &&
    ((event.deltaY < 0 && element.scrollTop > 0) ||
      (event.deltaY > 0 &&
        element.scrollTop < element.scrollHeight - element.clientHeight - 1));
  const canMoveX =
    scrollableX &&
    element.scrollWidth > element.clientWidth &&
    ((event.deltaX < 0 && element.scrollLeft > 0) ||
      (event.deltaX > 0 &&
        element.scrollLeft < element.scrollWidth - element.clientWidth - 1));
  return canMoveY || canMoveX;
}

function layerCanConsumeWheel(target: EventTarget | null, event: WheelEvent) {
  const layer = closestScrollLayer(target);
  if (!layer) return false;
  let element = target instanceof HTMLElement ? target : layer;
  while (true) {
    if (elementCanConsumeWheel(element, event)) return true;
    if (element === layer) return false;
    element = element.parentElement || layer;
  }
}

function isLayerScrollTarget(target: EventTarget | null) {
  return closestScrollLayer(target) !== null;
}

function restoreLockedScrollPositions() {
  if (window.scrollY !== lockedScrollY) window.scrollTo(0, lockedScrollY);
  for (const { element, scrollTop } of lockedSidebarScroll) {
    if (element.isConnected && element.scrollTop !== scrollTop) {
      element.scrollTop = scrollTop;
    }
  }
}

function preventPageScroll(event: Event) {
  if (event instanceof WheelEvent) {
    if (event.ctrlKey || layerCanConsumeWheel(event.target, event)) return;
    event.preventDefault();
    return;
  }
  if (isLayerScrollTarget(event.target)) return;
  event.preventDefault();
}

function preventPageScrollKey(event: KeyboardEvent) {
  if (!scrollKeys.has(event.key) || isLayerScrollTarget(event.target)) return;
  const target = event.target;
  if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement || (target instanceof HTMLElement && target.isContentEditable)) return;
  event.preventDefault();
}

function preserveBackgroundScroll(event: Event) {
  if (event.target === document) {
    restoreLockedScrollPositions();
    return;
  }
  if (
    event.target instanceof HTMLElement &&
    lockedSidebarScroll.some(({ element }) => element === event.target)
  ) {
    restoreLockedScrollPositions();
  }
}

function addPageScrollBlockers() {
  window.addEventListener("wheel", preventPageScroll, { capture: true, passive: false });
  window.addEventListener("touchmove", preventPageScroll, { capture: true, passive: false });
  document.addEventListener("keydown", preventPageScrollKey, true);
  document.addEventListener("scroll", preserveBackgroundScroll, true);
}

function removePageScrollBlockers() {
  window.removeEventListener("wheel", preventPageScroll, true);
  window.removeEventListener("touchmove", preventPageScroll, true);
  document.removeEventListener("keydown", preventPageScrollKey, true);
  document.removeEventListener("scroll", preserveBackgroundScroll, true);
}

export function acquirePageScrollLock() {
  if (activeLocks === 0) {
    lockedScrollY = window.scrollY;
    lockedSidebarScroll = Array.from(
      document.querySelectorAll<HTMLElement>(".sidebar:not(.menu-open) .nav"),
      (element) => ({ element, scrollTop: element.scrollTop }),
    );
    document.documentElement.classList.add("page-scroll-locked");
    document.body.classList.add("page-scroll-locked");
    addPageScrollBlockers();
    restoreLockedScrollPositions();
    restoreFrame = window.requestAnimationFrame(restoreLockedScrollPositions);
  }
  activeLocks += 1;
}

export function releasePageScrollLock() {
  activeLocks = Math.max(0, activeLocks - 1);
  if (activeLocks !== 0) return;

  document.documentElement.classList.remove("page-scroll-locked");
  document.body.classList.remove("page-scroll-locked");
  removePageScrollBlockers();
  if (restoreFrame !== undefined) window.cancelAnimationFrame(restoreFrame);
  restoreFrame = undefined;
  restoreLockedScrollPositions();
  lockedSidebarScroll = [];
}

export function usePageScrollLock(locked: boolean) {
  useLayoutEffect(() => {
    if (!locked) return;
    acquirePageScrollLock();
    return releasePageScrollLock;
  }, [locked]);
}
